/* This example requires Tailwind CSS v2.0+ */

import {
  ArchiveIcon,
  ChartSquareBarIcon,
  MicrophoneIcon,
  StatusOnlineIcon,
  TranslateIcon,
  UserGroupIcon,
  LockClosedIcon,
  UploadIcon,
  VolumeUpIcon,
  VideoCameraIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import Link from 'next/link';

import TranscriptionLanguages from './audio/TranscriptionLanguages';
import { FaYoutube } from 'react-icons/fa6';
import ZapierSVG from '~/svg/Zapier.svg';

const options = [
  {
    name: 'Audio to Text Converter',
    type: 'audio',
    icon: VolumeUpIcon,
    description: 'Convert audio files to text with high accuracy.',
    href: '/ai-transcription/audio-to-text-converter',
  },
  {
    name: 'Video to Text Converter',
    type: 'video',
    icon: VideoCameraIcon,
    description: 'Extract text from video files quickly and easily.',
    href: '/ai-transcription/video-to-text-converter',
  },
  {
    name: 'Transcribe a YouTube Video',
    type: 'youtube',
    icon: FaYoutube,
    description: 'Transcribe any YouTube video into readable text.',
  },
  {
    name: 'Record Audio',
    type: 'mic',
    icon: MicrophoneIcon,
    description: 'Record audio from your microphone and transcribe it to text.',
  },
  // Zapier
  {
    name: 'Integrate with Zapier',
    type: 'zapier',
    icon: ZapierSVG,
    description: 'Transcribe various media from Zapier to generate AI content.',
  },
];

const features = [
  {
    name: 'Human-Level Accuracy.',
    description:
      'Our AI harnesses the power of machine learning to provide outstanding speech recognition.',
    icon: ChartSquareBarIcon,
  },
  {
    name: 'Speaker Detection.',
    description:
      'It can distinguish between different speakers with ease, bringing clarity to your transcriptions.',
    icon: UserGroupIcon,
  },
  {
    name: 'Multilingual Support.',
    description:
      'Our AI supports over 38 languages, making it your perfect ally in an increasingly connected world.',
    icon: TranslateIcon,
  },
  {
    name: 'Versatile Transcription.',
    description:
      'From Zoom meetings and podcasts to lectures and more, our AI has got it covered.',
    icon: StatusOnlineIcon,
  },
  {
    name: 'Summarize it.',
    description:
      'Need a concise summary? Our AI can distil the gist from hours of conversation.',
    icon: ArchiveIcon,
  },
  {
    name: 'Privacy.',
    description:
      'Your Privacy Matters. We securely process your files, never share them with third parties, and allow you to delete files after usage, ensuring your data remains confidential and protected.',
    icon: LockClosedIcon,
  },
];

export default function TranscriptionFeaturesSimple() {
  const { t } = useLocale('ai-transcription');

  return (
    <section>
      <div className='relative isolate bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm'>
        <div className='absolute inset-0 -z-10 bg-[radial-gradient(#80808033_1px,transparent_1px)] bg-[size:20px_20px] [mask-image:radial-gradient(ellipse_80%_80%_at_50%_0%,#000_10%,transparent_100%)] opacity-40'></div>
        <div className='mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:grid lg:grid-cols-3 lg:gap-x-12 lg:px-8 lg:py-24'>
          <div className='flex flex-col justify-center'>
            <h2 className='inline-flex w-max items-center rounded-full bg-primary-50 px-3 py-1 text-sm font-black uppercase tracking-wider text-primary-600 ring-1 ring-inset ring-primary-600/20 dark:bg-primary-500/10 dark:text-primary-400 dark:ring-primary-500/20'>
              <svg
                className='w-4 h-4 mr-2'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M13 10V3L4 14h7v7l9-11h-7z'
                />
              </svg>
              {t('Core Features')}
            </h2>
            <p className='leading-tight mt-6 text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-5xl text-balance'>
              {t('Indulge in the convenience of effortless transcription.')}
            </p>
            <p className='mt-6 text-lg text-gray-600 dark:text-slate-400'>
              Say adieu to manual transcriptions and language barriers. Get
              started today!
            </p>
            <div className='mt-8'>
              <Link
                href='/audios'
                className='inline-flex rounded-full bg-gray-900 dark:bg-white px-6 py-3 text-sm font-bold text-white dark:text-gray-900 shadow-sm hover:shadow-lg transition-all hover:-translate-y-0.5 hover:bg-gray-800 dark:hover:bg-gray-100'
              >
                {t('Get Started Free')}
              </Link>
            </div>
          </div>
          <div className='mt-16 lg:col-span-2 lg:mt-0'>
            <dl className='grid grid-cols-1 gap-8 sm:grid-cols-2'>
              {features.map((feature) => (
                <div
                  key={feature.name}
                  className='group relative rounded-3xl bg-white/70 p-8 shadow-sm ring-1 ring-gray-900/5 backdrop-blur-md dark:bg-slate-800/40 dark:ring-white/10 hover:-translate-y-1 hover:shadow-xl hover:ring-primary-500/30 transition-all duration-300'
                >
                  <div className='absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 dark:from-primary-900/20 rounded-3xl' />
                  <dt className='relative'>
                    <div className='flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-100 shadow-inner ring-1 ring-gray-900/5 dark:bg-primary-900/30 dark:ring-white/10 group-hover:scale-110 group-hover:bg-primary-500 transition-all duration-300'>
                      <feature.icon
                        className='h-6 w-6 text-primary-700 dark:text-primary-400 group-hover:text-white transition-colors duration-300'
                        aria-hidden='true'
                      />
                    </div>
                    <p className='mt-6 text-xl font-bold leading-6 text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors'>
                      {t(feature.name)}
                    </p>
                  </dt>
                  <dd className='relative mt-3 text-base leading-relaxed text-gray-600 dark:text-slate-400'>
                    {t(feature.description)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
      <TranscriptionLanguages />
      <div className='relative isolate bg-gray-50/80 py-24 dark:bg-slate-900/80 backdrop-blur-md border-t border-gray-200 dark:border-slate-800'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10'>
          <h2 className='mx-auto max-w-4xl text-center text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-5xl text-balance'>
            {t(
              'Comprehensive Transcription Capabilities for Diverse Audio Inputs',
            )}
          </h2>
          <p className='mx-auto mt-6 max-w-2xl text-center text-xl text-gray-600 dark:text-slate-400'>
            {t(
              'Accurate, fast, and reliable text transcriptions from any audio source. Start converting today with a few clicks.',
            )}
          </p>
          <dl className='mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5'>
            {options.map((feature) => {
              const content = (
                <div className='relative flex flex-col items-center p-8 bg-white dark:bg-slate-800 rounded-3xl shadow-md ring-1 ring-gray-900/5 dark:ring-white/10 h-full group-hover:-translate-y-2 group-hover:shadow-2xl group-hover:ring-primary-500/50 transition-all duration-500 text-center overflow-hidden'>
                  <div className='absolute inset-0 bg-gradient-to-b from-transparent to-primary-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 dark:to-primary-900/10' />
                  <div className='relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-50 shadow-sm ring-1 ring-inset ring-gray-900/5 dark:bg-slate-900 dark:ring-white/5 group-hover:scale-110 group-hover:bg-primary-50 group-hover:ring-primary-500/20 dark:group-hover:bg-primary-900/30 transition-all duration-500'>
                    <feature.icon
                      className='h-8 w-8 text-gray-600 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-500 drop-shadow-sm'
                      aria-hidden='true'
                    />
                  </div>
                  <strong className='relative mt-8 text-lg font-extrabold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors text-balance'>
                    {t(feature.name)}
                  </strong>
                  <dd className='relative mt-3 text-sm leading-relaxed text-gray-500 dark:text-slate-400'>
                    {t(feature.description)}
                  </dd>
                </div>
              );

              if (feature.href) {
                return (
                  <Link
                    key={feature.name}
                    href={feature.href}
                    className='group block h-full outline-none'
                  >
                    {content}
                  </Link>
                );
              }

              return (
                <div key={feature.name} className='group block h-full'>
                  {content}
                </div>
              );
            })}
          </dl>
        </div>
      </div>
      {/* <div className='bg-white py-24 dark:bg-slate-900'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl lg:text-center'>
            <h3 className='mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
              {t('AI Transcription with speakers detection')}
            </h3>
          </div>
          <div className='mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-4xl'>
            <LiteYouTubeEmbed
              id='EKS3S0XOGNs'
              title='AI Transcription'
              poster='hqdefault'
            />
          </div>
        </div>
      </div> */}
    </section>
  );
}
